import type {
  ReactNode,
} from 'react'
import {
  useCallback,
  useState,
} from 'react'
import {
  useQuery,
} from '@tanstack/react-query'
import {
  featuredIntegrationsService,
  getApiErrorMessage,
} from '../services/api'
import type {
  FeaturedIntegrationSummary,
  PipedreamAppConnectionResponse,
} from '../client'
import {
  usePipedreamConnections,
} from '../hooks/usePipedreamConnections'
import {
  pipedreamAppSuggestion,
  useSuggestionConnect,
} from '../hooks/useSuggestionConnect'
import {
  DisconnectModal,
} from './PromotedIntegrations'
import {
  PairingModalShell,
} from './pairing/PairingModalShell'
import {
  IntegrationSuggestionSurfaces,
} from '../analytics/posthog'
import {
  canAddPipedreamAccount,
  pipedreamAccountName,
  pipedreamAccountsOf,
  pipedreamConnectionTitle,
  pipedreamConnectLanded,
} from '../utils/pipedreamAccounts'

/**
 * Pipedream apps Catch presents as tiles of its own, inside the category card
 * each one belongs to, rather than leaving them to be found by searching the
 * "Connect any app" catalog — which `pipedream-catalog-browse` hides from
 * everyone outside the internal cohort.
 *
 * **The list is served, not hardcoded here.** It shipped as a table in this
 * file (GET-14067) and moved to `app/constants/featured_integrations.py`,
 * because a slug living in the frontend sat outside
 * `verify_pipedream_catalog.py`'s sweep: an app Pipedream RETIRED stayed
 * invisible until a user tapped the tile and got a 404 out of
 * `create_connect_url`, and no frontend test can see that — the slug that
 * breaks is precisely the one nobody edited. Adding an app is now one backend
 * entry, and its category placement follows from the descriptor.
 *
 * What stays here is the art. See `FeaturedAppIcon`.
 */
const FEATURED_QUERY_KEY = ['featured-integrations']

export interface FeaturedPipedreamApps {
  apps: FeaturedIntegrationSummary[]
  /** Featured apps for one category card, in the order the backend listed them. */
  forCategory: (category: string) => FeaturedIntegrationSummary[]
  /** Set while the first read of the list or the connections is in flight. */
  loading: boolean
  error: string | null
  isConnected: (app: FeaturedIntegrationSummary) => boolean
  /** The app's connected accounts, oldest first. */
  accounts: (app: FeaturedIntegrationSummary) => PipedreamAppConnectionResponse[]
  /** True while this app's own connect or disconnect is running. */
  isPending: (app: FeaturedIntegrationSummary) => boolean
  /** Any featured app in this category is connected — drives the progress bar. */
  categoryConnected: (category: string) => boolean
  /** Connect the app; with `accountLabel`, add another account under that name. */
  connect: (app: FeaturedIntegrationSummary, accountLabel?: string) => void
  disconnect: (app: FeaturedIntegrationSummary) => void
  /** Drop one account, leaving the app's others connected. */
  disconnectAccount: (connection: PipedreamAppConnectionResponse) => void
  /** Re-read the shared connection list — for a connect made on another card. */
  refresh: () => void
}

/**
 * The featured list, the page's one read of the user's Pipedream connections,
 * and the connect/disconnect flows over them.
 *
 * The connect flow is `useSuggestionConnect`, unchanged — it owns the popup,
 * the mobile-redirect watchdog, the abandoned-attempt settle and the funnel
 * events, none of which a second hand-rolled copy would have (GET-11630,
 * GET-13089).
 */
export function useFeaturedPipedreamApps(
  { enabled }: { enabled: boolean },
): FeaturedPipedreamApps {
  const listQuery = useQuery({
    queryKey: FEATURED_QUERY_KEY,
    queryFn: () => featuredIntegrationsService.list(),
    enabled,
  })
  const {
    connections,
    loading: connectionsLoading,
    error: connectionsError,
    refresh,
    disconnect: disconnectRows,
  } = usePipedreamConnections({ enabled })
  const [error, setError] = useState<string | null>(null)
  const [disconnectingSlug, setDisconnectingSlug] = useState<string | null>(null)

  const {
    connectingKey,
    error: connectError,
    clearError: clearConnectError,
    connect: startConnect,
  } = useSuggestionConnect({
    surface: IntegrationSuggestionSurfaces.INTEGRATIONS_CATEGORY_TILE,
    onSettled: async (suggestion) => {
      const loaded = await refresh()
      if (loaded === null) return 'unknown'
      return pipedreamConnectLanded(
        loaded,
        suggestion.catalog_key,
        suggestion.account_label,
      )
        ? 'connected'
        : 'not_connected'
    },
  })

  // Both error channels are cleared by whichever action runs next, so the
  // newest failure is the one on screen whichever side raised it. Without this
  // a stale connect failure would outrank every later error, which is the
  // hazard `PipedreamIntegrationsCard` documents and solves the same way.
  const clearErrors = useCallback(() => {
    setError(null)
    clearConnectError()
  }, [clearConnectError])

  const connect = useCallback((
    app: FeaturedIntegrationSummary,
    accountLabel?: string,
  ) => {
    clearErrors()
    void startConnect(pipedreamAppSuggestion(
      app.app_slug,
      app.display_name,
      app.logo_url ?? null,
      accountLabel,
    ))
  }, [clearErrors, startConnect])

  // Rows of one app: all of them (`disconnect`) or a single account.
  const dropRows = useCallback((rows: PipedreamAppConnectionResponse[]) => {
    const first = rows[0]
    if (!first) return
    void (async () => {
      setDisconnectingSlug(first.app_slug)
      clearErrors()
      try {
        await disconnectRows(rows)
      } catch (err) {
        setError(getApiErrorMessage(err, `Could not disconnect ${first.name}.`))
      } finally {
        setDisconnectingSlug(null)
        await refresh()
      }
    })()
  }, [clearErrors, disconnectRows, refresh])

  // Every connection for this app, not just the first. A tile that presents
  // the app as one thing (Drive's chooser, Teams' picker) would otherwise flip
  // straight back to connected with nothing on screen saying why. A tile that
  // lists the accounts drops them one at a time with `disconnectAccount`.
  const disconnect = useCallback((app: FeaturedIntegrationSummary) => {
    dropRows(pipedreamAccountsOf(connections, app.app_slug))
  }, [connections, dropRows])

  const disconnectAccount = useCallback((
    connection: PipedreamAppConnectionResponse,
  ) => {
    dropRows([connection])
  }, [dropRows])

  const apps = listQuery.data ?? []
  const isConnected = (app: FeaturedIntegrationSummary) =>
    connections.some((c) => c.app_slug === app.app_slug)
  const forCategory = (category: string) =>
    apps.filter((app) => app.category === category)

  return {
    apps,
    forCategory,
    loading: enabled && (listQuery.isPending || connectionsLoading),
    error: connectError
      ?? error
      ?? connectionsError
      ?? (listQuery.isError
        ? getApiErrorMessage(listQuery.error, 'Could not load integrations.')
        : null),
    isConnected,
    accounts: (app) => pipedreamAccountsOf(connections, app.app_slug),
    isPending: (app) =>
      connectingKey === app.app_slug || disconnectingSlug === app.app_slug,
    categoryConnected: (category) => forCategory(category).some(isConnected),
    connect,
    disconnect,
    disconnectAccount,
    refresh: () => { void refresh() },
  }
}

/**
 * The tile's artwork: our own mark where we have drawn one, Pipedream's brand
 * asset where the descriptor carries one, and a monogram otherwise.
 *
 * **Why local art comes first.** These tiles were asked to look like the
 * first-party ones beside them, and the alternative on the table —
 * `DynamicIntegrationTile`, which the promoted registry uses — is visibly a
 * different tile: different chrome, its own monogram fallback. What was never
 * the difference is where the pixels come from: `IntegrationTile` already
 * renders several first-party tiles from `<img>` files (Monday, Zoho, Otter),
 * so a remote logo inside it is indistinguishable from a local one. That is
 * what makes the fallback chain safe rather than a downgrade — and it is what
 * lets a fifth app be a backend-only edit that still renders something
 * reasonable on the day it ships, with hand-drawn art as a later upgrade
 * rather than a precondition.
 */
export function FeaturedAppIcon({
  app,
  localIcon,
  className = 'w-8 h-8',
}: {
  app: FeaturedIntegrationSummary
  localIcon?: ReactNode | undefined
  className?: string
}) {
  if (localIcon) return <>{localIcon}</>
  if (app.logo_url) {
    return (
      <img
        src={app.logo_url}
        alt=""
        className={`${className} object-contain rounded`}
      />
    )
  }
  return <MonogramIcon name={app.display_name} className={className} />
}

/**
 * The "no art yet" affordance: an app's initial on the neutral chip.
 *
 * Exported because it is a convention rather than a style — a tile rendering
 * its own coloured monogram reads as a brand chip, and then does not follow
 * when this one changes.
 */
export function MonogramIcon({
  name,
  className = 'w-8 h-8',
}: {
  name: string
  className?: string
}) {
  return (
    <div
      className={`${className} rounded bg-primary-50 text-primary-700 flex items-center justify-center text-sm font-semibold`}
      aria-hidden="true"
    >
      {name.slice(0, 1).toUpperCase()}
    </div>
  )
}

/**
 * The Google Drive tile's two routes, side by side.
 *
 * Drive is the one app a user can reach two ways, and they are not
 * interchangeable: the Google sign-in we already hold carries
 * `drive.readonly`, so Catch can find and read files but cannot create or edit
 * one. Connecting Drive through Pipedream is what buys the write half. Rather
 * than guess which the user wants — or silently give them the weaker one and
 * let them discover the gap when the assistant refuses an edit — the tile asks.
 *
 * Which apps get this is the descriptor's call, not this file's: the backend
 * sets `read_only_scope_cluster` on an app that also has a first-party
 * read-only route. `readOnly` is null when the user cannot take that route at
 * all — they did not sign in with Google — leaving the read-write one as the
 * only offer. Both can be held at once; that is a normal state, not a conflict.
 *
 * The read-write route can hold several accounts, a personal Drive and a work
 * one (GET-19842). With more than one, each is listed and removed on its own:
 * a single Disconnect under the bare name "Google Drive" would take them all.
 */
export function GoogleDriveAccessModal({
  icon,
  readOnly,
  fullAccess,
  onClose,
}: {
  icon: ReactNode
  readOnly: {
    connected: boolean
    loading: boolean
    onConnect: () => void
  } | null
  fullAccess: {
    connected: boolean
    loading: boolean
    onConnect: () => void
    onDisconnect: () => void
    /** The read-write accounts, oldest first. */
    accounts?: PipedreamAppConnectionResponse[] | undefined
    /** Add another read-write account; offered when the app takes more. */
    onAddAccount?: (() => void) | undefined
    onDisconnectAccount?: ((account: PipedreamAppConnectionResponse) => void) | undefined
  }
  onClose: () => void
}) {
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false)
  const [removing, setRemoving] = useState<PipedreamAppConnectionResponse | null>(null)
  const accounts = fullAccess.accounts ?? []
  const onDisconnectAccount = fullAccess.onDisconnectAccount
  const several = accounts.length > 1 && onDisconnectAccount !== undefined
  const onAddAccount = fullAccess.onAddAccount
  const addAccount = onAddAccount && canAddPipedreamAccount(accounts)
    ? (
      <ActionButton
        label="Add another"
        loading={fullAccess.loading}
        onClick={onAddAccount}
      />
    )
    : null

  return (
    <>
      <PairingModalShell
        isOpen
        onClose={onClose}
        closeAriaLabel="Close"
        title={(
          <span className="flex items-center gap-2">
            <span className="w-6 h-6 flex items-center justify-center">{icon}</span>
            Google Drive access
          </span>
        )}
      >
        <p className="text-sm text-gray-500 mb-5">
          Choose how much of your Drive your assistant can reach.
        </p>

        <div className="space-y-3">
          {readOnly && (
            <AccessOption
              title="Read only"
              description="Find, open and read your files. Included with your Google sign-in."
              connected={readOnly.connected}
              // Google grants every scope in one consent, so there is no
              // per-service revoke for us to offer — the same reason the
              // scope-managed tiles show `GoogleScopeInfoModal` instead of a
              // disconnect.
              note={readOnly.connected ? 'Managed in your Google Account' : undefined}
              action={readOnly.connected
                ? <span className="text-xs font-semibold text-green-700">Connected</span>
                : (
                  <ActionButton
                    label="Allow"
                    loading={readOnly.loading}
                    onClick={readOnly.onConnect}
                  />
                )}
            />
          )}
          {/* Self-contained rather than "everything above, plus…": the row
              above it is withheld from a user who did not sign in with Google,
              and a comparative that compares to nothing is worse than a
              slightly longer sentence. Named for what it grants, not "full
              access" — deletes are deliberately not served (`allow_delete`
              stays off for Drive in `openapi_tool_catalog`), so "full" would
              claim more than we do. */}
          <AccessOption
            title="Read & write"
            description="Find, open, create, edit and organise your files."
            connected={fullAccess.connected}
            action={fullAccess.connected
              ? (
                <div className="flex items-center gap-2">
                  {addAccount}
                  {!several && (
                    <ActionButton
                      label="Disconnect"
                      destructive
                      loading={fullAccess.loading}
                      onClick={() => setConfirmingDisconnect(true)}
                    />
                  )}
                </div>
              )
              : (
                <ActionButton
                  label="Connect"
                  loading={fullAccess.loading}
                  onClick={fullAccess.onConnect}
                />
              )}
          />
          {several && (
            <ul className="space-y-2">
              {accounts.map((account) => (
                <li
                  key={account.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 rounded-lg"
                >
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {pipedreamAccountName(account)}
                  </span>
                  <button
                    onClick={() => setRemoving(account)}
                    disabled={fullAccess.loading}
                    aria-label={`Remove ${pipedreamConnectionTitle(account)}`}
                    className="flex-shrink-0 text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </PairingModalShell>

      {/* The same confirmation every other disconnect on this page goes
          through, so this one is neither unconfirmed nor uncounted. */}
      {confirmingDisconnect && (
        <DisconnectModal
          name="Google Drive"
          onConfirm={() => { onClose(); fullAccess.onDisconnect() }}
          onClose={() => setConfirmingDisconnect(false)}
        />
      )}
      {removing && onDisconnectAccount && (
        <DisconnectModal
          name={pipedreamConnectionTitle(removing)}
          onConfirm={() => { onClose(); onDisconnectAccount(removing) }}
          onClose={() => setRemoving(null)}
        />
      )}
    </>
  )
}

function AccessOption({
  title,
  description,
  connected,
  note,
  action,
}: {
  title: string
  description: string
  connected: boolean
  note?: string | undefined
  /** The row's control, chosen by the caller — this component only lays out. */
  action: ReactNode
}) {
  return (
    <div
      className={`flex items-center justify-between gap-4 p-4 rounded-xl border ${
        connected ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
      }`}
    >
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        {note && <p className="text-[11px] text-gray-400 mt-1">{note}</p>}
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  )
}

function ActionButton({
  label,
  loading,
  destructive = false,
  onClick,
}: {
  label: string
  loading: boolean
  destructive?: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50 ${
        destructive
          ? 'text-red-600 font-medium hover:bg-red-50'
          : 'text-white bg-primary-600 hover:bg-primary-700'
      }`}
    >
      {loading ? 'Working…' : label}
    </button>
  )
}
